import type { MovementTuning } from '@battle/content';
import { Pane } from 'tweakpane';
import type { Game } from '../game/game.ts';

/**
 * Training-mode tuning panel (docs/09 M1): live-edit movement, camera, input and animation
 * feel. Toggle with the backquote key (`) or open with ?tune. Tweaks persist in this browser;
 * "Copy as TS" puts paste-ready values for packages/content and the client on the clipboard.
 */
const STORAGE_KEY = 'battle.tuning.v1';

type Saved = { movement?: MovementTuning; camera?: object; input?: object; view?: object };

function load(): Saved {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Saved;
  } catch {
    return {};
  }
}

export function createTuningPanel(game: Game, open: boolean) {
  const t = game.tuning;
  const defaults = {
    movement: structuredClone(t.movement()),
    camera: { ...t.camera },
    input: { ...t.input },
    view: { ...t.view },
  };

  // Restore this browser's tweaks.
  const saved = load();
  const movement = structuredClone(saved.movement ?? t.movement());
  Object.assign(t.camera, saved.camera);
  Object.assign(t.input, saved.input);
  Object.assign(t.view, saved.view);
  t.setMovement(structuredClone(movement));

  const container = document.createElement('div');
  container.className = 'tuning-panel';
  document.body.appendChild(container);
  const pane = new Pane({ container, title: 'Tuning (`)', expanded: true });
  container.hidden = !open;

  const persist = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ movement, camera: t.camera, input: t.input, view: t.view }),
      );
    } catch {
      // Not persisted.
    }
  };
  const applyMovement = () => {
    t.setMovement(structuredClone(movement));
    persist();
  };

  const mv = pane.addFolder({ title: 'Movement' });
  const num = (
    folder: typeof mv,
    obj: object,
    key: string,
    min: number,
    max: number,
    step?: number,
    label?: string,
  ) =>
    folder
      .addBinding(obj as Record<string, number>, key, { min, max, step, label: label ?? key })
      .on(
        'change',
        obj === t.camera || obj === t.input || obj === t.view ? persist : applyMovement,
      );
  num(mv, movement, 'walkSpeed', 0.5, 5, 0.1);
  num(mv, movement, 'runSpeed', 2, 12, 0.1);
  num(mv, movement, 'sprintSpeed', 4, 16, 0.1);
  num(mv, movement, 'accel', 5, 120, 1);
  num(mv, movement, 'decel', 5, 120, 1);
  num(mv, movement, 'airAccel', 0, 60, 1);
  num(mv, movement, 'turnRate', 90, 1440, 10);
  num(mv, movement, 'turnRateSharp', 90, 2000, 10);
  num(mv, movement, 'jumpVelocity', 2, 14, 0.1);
  num(mv, movement, 'gravity', 5, 60, 0.5);
  num(mv, movement, 'fallGravityScale', 1, 3, 0.05);
  num(mv, movement, 'coyoteTicks', 0, 20, 1);
  num(mv, movement, 'bufferTicks', 0, 30, 1);
  const dg = mv.addFolder({ title: 'Dodge', expanded: false });
  num(dg, movement.dodge, 'speed', 4, 30, 0.5);
  num(dg, movement.dodge, 'ticks', 4, 40, 1);
  num(dg, movement.dodge, 'iframeTicks', 0, 30, 1);
  num(dg, movement.dodge, 'recoveryTicks', 0, 30, 1);
  num(dg, movement.dodge, 'stamina', 0, 60, 1);
  const st = mv.addFolder({ title: 'Stamina', expanded: false });
  num(st, movement.stamina, 'regenPerSec', 5, 150, 1);
  num(st, movement.stamina, 'regenDelayTicks', 0, 120, 1);
  num(st, movement.stamina, 'sprintDrainPerSec', 0, 80, 1);

  const cf = pane.addFolder({ title: 'Camera', expanded: false });
  num(cf, t.camera, 'distance', 2, 12, 0.1);
  num(cf, t.camera, 'height', 0.3, 3, 0.05);
  num(cf, t.camera, 'shoulder', -1.5, 1.5, 0.05);
  num(cf, t.camera, 'followLag', 0, 0.4, 0.01);
  num(cf, t.camera, 'fov', 35, 85, 1);
  num(cf, t.camera, 'sprintFov', 35, 95, 1);
  num(cf, t.camera, 'lockOnFraming', 0, 1, 0.05);

  const inf = pane.addFolder({ title: 'Input', expanded: false });
  num(inf, t.input, 'mouseSensitivity', 0.0005, 0.008, 0.0001, 'mouse');
  inf.addBinding(t.input, 'invertY').on('change', persist);
  num(inf, t.input, 'padLookSpeed', 1, 8, 0.1, 'pad look');
  num(inf, t.input, 'deadzone', 0, 0.4, 0.01);
  num(inf, t.input, 'responseExponent', 1, 3, 0.05, 'pad curve');

  const vf = pane.addFolder({ title: 'Animation', expanded: false });
  num(vf, t.view, 'walkRef', 0.5, 4, 0.05, 'walk clip m/s');
  num(vf, t.view, 'runRef', 2, 10, 0.05, 'run clip m/s');
  num(vf, t.view, 'leanPerYawRate', 0, 0.2, 0.005, 'lean');
  num(vf, t.view, 'leanMax', 0, 0.5, 0.01);
  num(vf, t.view, 'squash', 0, 0.4, 0.01);

  pane.addBlade({ view: 'separator' });
  pane.addButton({ title: 'Reset position' }).on('click', () => game.tuning.resetPlayer());
  pane.addButton({ title: 'Copy as TS' }).on('click', async (ev) => {
    const text = [
      '// packages/content/src/movement.ts',
      `export const DEFAULT_MOVEMENT: MovementTuning = ${JSON.stringify(movement, null, 2)};`,
      '',
      '// apps/client/src/camera/follow-camera.ts',
      `export const DEFAULT_CAMERA: CameraTuning = ${JSON.stringify(t.camera, null, 2)};`,
      '',
      '// apps/client/src/input/input.ts',
      `export const DEFAULT_INPUT_SETTINGS: InputSettings = ${JSON.stringify(t.input, null, 2)};`,
      '',
      '// apps/client/src/creatures/creature-view.ts (this creature)',
      JSON.stringify(t.view, null, 2),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      ev.target.title = 'Copied ✓';
    } catch {
      console.log(text);
      ev.target.title = 'Copied to console';
    }
    setTimeout(() => {
      ev.target.title = 'Copy as TS';
    }, 1500);
  });
  pane.addButton({ title: 'Reset to defaults' }).on('click', () => {
    // Nested objects are bound by reference: copy into them, don't replace them.
    const d = structuredClone(defaults.movement);
    Object.assign(movement.dodge, d.dodge);
    Object.assign(movement.stamina, d.stamina);
    Object.assign(movement.brace, d.brace);
    Object.assign(movement.capsule, d.capsule);
    Object.assign(movement, {
      ...d,
      dodge: movement.dodge,
      stamina: movement.stamina,
      brace: movement.brace,
      capsule: movement.capsule,
    });
    Object.assign(t.camera, defaults.camera);
    Object.assign(t.input, defaults.input);
    Object.assign(t.view, defaults.view);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    t.setMovement(structuredClone(movement));
    pane.refresh();
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.code !== 'Backquote') return;
    container.hidden = !container.hidden;
    if (!container.hidden) document.exitPointerLock?.();
  };
  window.addEventListener('keydown', onKey);
  return { pane, dispose: () => window.removeEventListener('keydown', onKey) };
}
